import { useState } from 'react';

import { IconDotsVertical, IconX } from '@tabler/icons-react';
import { useAtom } from 'jotai';

import { LocalMarketData } from 'pages/Settings/LocalMarketSettings/api/useLocalMarket/useLocalMarket';
import { activeDealerTypeAtom } from 'pages/Settings/LocalMarketSettings/DealerSidebar';
import { CircleIcon } from 'pages/Settings/LocalMarketSettings/icons/CircleIcon';
import { HexIcon } from 'pages/Settings/LocalMarketSettings/icons/HexIcon';
import { StarIcon2 } from 'pages/Settings/LocalMarketSettings/icons/StarIcon';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Panel from '@lotlinx-aws/sparky-component-library/Sparky/Panel';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import Tooltip from '@lotlinx-aws/sparky-component-library/Sparky/Tooltip';
import { formatNumber } from 'util/formatters';

import styles from './SummaryPanel.module.scss';

export interface SummaryPanelProps {
  selectedHexs: string[];
  data: LocalMarketData | undefined;
}

export const legendItems = [
  {
    icon: (
      <div className={styles.iconPair}>
        <StarIcon2 color={COLOR.SPARKY_PURPLE_400} />
        <StarIcon2 color={COLOR.SPARKY_YELLOW_500} />
      </div>
    ),
    label: 'Key dealerships',
  },
  {
    icon: <CircleIcon color={COLOR.SPARKY_NAVY_400} />,
    label: 'Your dealership',
  },
  {
    icon: <CircleIcon color='#a90df866' />,
    label: 'Franchise dealership',
  },
  {
    icon: <CircleIcon color='#B0943666' />,
    label: 'Independent dealership',
  },
  {
    icon: <HexIcon color={COLOR.SPARKY_BLUE_600} />,
    label: 'Selected In-market cells',
  },
  // {
  //   icon: <HexIcon color={COLOR.SPARKY_GREY_300} />,
  //   label: 'Minimum in-market cells',
  // },
];

/**
 * SummaryPanel
 */
export default function SummaryPanel({ selectedHexs, data }: SummaryPanelProps) {
  const [isSummaryPanelOpen, setIsSummaryPanelOpen] = useState(false);
  const [activeDealerType] = useAtom(activeDealerTypeAtom);
  if (!data) {
    return <></>;
  }

  const dataMax = Math.max(...selectedHexs.map((hex) => data?.hexData[hex]?.inMarketShoppers));

  const dataArray = selectedHexs.map((hex) => data.hexData[hex]);

  const summaryData = {
    totalDemand: {
      title: 'Total Shoppers',
      tooltip: 'Estimate of all potential shoppers in selected PMA',
      subText: 'In-market shoppers',
      value: formatNumber(dataArray.reduce((acc, curr) => acc + (curr?.inMarketShoppers ?? 0), 0)),
    },
    totalDealerships: {
      title: 'Total dealerships',
      tooltip: 'All dealers located in selected PMA',
      subText: 'Dealers in selected cells',
      value: formatNumber(
        dataArray.reduce(
          (acc, curr) =>
            acc + (activeDealerType === 'new' ? curr?.dealerCountNew : curr?.dealerCountUsed),
          0,
        ),
      ),
    },
    totalListedUnits: {
      title: 'Total listed units',
      tooltip: 'All units located in selected PMA',
      subText: 'Units in selected cells',
      value: formatNumber(
        dataArray.reduce(
          (acc, curr) =>
            acc + (activeDealerType === 'new' ? curr?.newListedUnits : curr?.usedListedUnits),
          0,
        ),
      ),
    },
  };

  // CLOSED STATE
  if (!isSummaryPanelOpen) {
    return (
      <div className={styles.closedControlPanel}>
        <div className={styles.compactSummaryContainer}>
          {Object.values(summaryData).map((block) => (
            <div className={styles.compactSummary} key={block.title}>
              <Tooltip content={block.tooltip} side='top'>
                <div>
                  <Text size='16' underline dottedUnderline type='body-compact-defined-01'>
                    {block.title}:
                  </Text>
                </div>
              </Tooltip>
              <Text size='16' type='body-compact-01'>
                {block.value}
              </Text>
            </div>
          ))}
          <IconButton
            onClick={() => setIsSummaryPanelOpen(true)}
            aria-label='toggle-control-panel'
            size='sm'
            variants='outline'
            tooltip='View more'
            tooltipSide='top'
          >
            <IconDotsVertical />
          </IconButton>
        </div>
      </div>
    );
  }

  // OPEN STATE
  return (
    <Panel className={styles.summaryPanel}>
      <div className={styles.topContainer}>
        <Flex width='100%' justifyContent='space-between'>
          <Flex gap='4px'>
            <Tooltip content='Primary market area' side='top'>
              <div>
                <Text color='secondary' type='label-defined-01' underline dottedUnderline>
                  PMA
                </Text>
              </div>
            </Tooltip>
            <Text color='secondary' type='label-defined-01'>
              summary
            </Text>
          </Flex>
          <IconButton
            onClick={() => setIsSummaryPanelOpen(false)}
            aria-label='toggle-control-panel'
            size='sm'
          >
            <IconX color={COLOR.SPARKY_GREY_400} />
          </IconButton>
        </Flex>

        {/* DATA SUMMARY */}
        <Flex column width='100%'>
          {Object.values(summaryData).map((block) => (
            <Flex
              key={block.title}
              justifyContent='space-between'
              width='100%'
              padding='10.5px 8px'
            >
              <Flex column gap='4px'>
                <Tooltip content={block.tooltip} side='top'>
                  <div>
                    <Text type='label-defined-03' underline dottedUnderline>
                      {block.title}
                    </Text>
                  </div>
                </Tooltip>
                <Text fontWeight={4} size='14'>
                  {block.subText}
                </Text>
              </Flex>

              <Text type='body-compact-01'>{block.value}</Text>
            </Flex>
          ))}
        </Flex>
      </div>

      <hr className={styles.divider} />

      {/* LEGEND */}
      <div className={styles.legendContainer}>
        <Text type='label-01' color='secondary'>
          Legend
        </Text>
        <div className={styles.legendItem}>
          {legendItems.map((item) => (
            <div key={item.label} className={styles.legendItemRow}>
              <div className={styles.legendIcon}>{item.icon}</div>
              <Text type='body-compact-01'>{item.label}</Text>
            </div>
          ))}
        </div>

        {/* COLOR SCALE */}
        <div className={styles.scaleLegend}>
          <Text>In-market Shoppers</Text>
          <Flex justifyContent='space-between' alignItems='center' gap='8px' margin='12px 0 12px'>
            <Text size='12' type='helper-text-01' color='tertiary'>
              {0}
            </Text>
            <div
              className={styles.gradiantBar}
              style={{
                background: `linear-gradient(90deg, rgba(130, 175, 247, 0.00) -3.01%, ${COLOR.SPARKY_BLUE_400} 100%)`,
              }}
            />
            <Text size='12' type='helper-text-01' color='tertiary'>
              {formatNumber(dataMax)}
            </Text>
          </Flex>
        </div>
      </div>
    </Panel>
  );
}
